import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Headphones, Loader2, Phone, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { useCallTrackingSettings } from '@/hooks/useCallTracking';
import { useUserRole } from '@/hooks/useUserRole';
import { cn } from '@/lib/utils';
import type { CtmPhoneEmbedElement } from '@/types/ctm-phone-embed';
import { IncomingCallPop } from './IncomingCallPop';

/**
 * CTM softphone, embedded inline.
 *
 * Topology (verified against CTM's shipped scripts): <ctm-phone-embed> hosts
 * the phone UI iframe; after the first access token it injects
 * <ctm-device-embed> (the audio device / station, its own iframe with
 * microphone permission) as its next sibling. The two talk over a
 * BroadcastChannel that only works while both live under this page — which
 * is why a CTM-origin popup can never integrate with the CRM. So: one phone,
 * inline, with the device given a visible slot for its "activate audio" step.
 *
 * Access: the CRM mints a 12 h session (ctm-create-phone-access-ticket); the
 * widget POSTs the session URL whenever it needs credentials and the edge
 * function serves a fresh CTM token as needed. The CRM also refreshes
 * proactively at the server-provided sc_refresh_at, via
 * el.setAttribute('access') + el.getUserAccess() — never by remounting.
 */

const CTM_EMBED_SCRIPT = 'https://app.calltrackingmetrics.com/ctm-phone-embed-1.0.js';
// Web Locks name: exactly one tab per browser profile runs the phone (CTM's
// device holds its own single-instance lock too; this avoids booting a
// second widget that would only ever see "locked out").
const LEADER_LOCK = 'sc:softphone:leader';
export const SOFTPHONE_OPT_OUT_KEY = 'sc:softphone:disabled';
// Fallback refresh cadence when the access payload carries no sc_refresh_at.
const DEFAULT_REFRESH_MS = 45 * 60 * 1000;
// A ringing state with no follow-up event is a missed call.
const RINGING_WATCHDOG_MS = 90 * 1000;
// If nothing from the phone app after the token, reveal the device so the
// agent can see whatever it is asking for (station check / activation).
const SILENT_BOOT_MS = 15 * 1000;
const RECOVERY_BACKOFF_MS = [5_000, 15_000, 60_000, 180_000];

export type SoftphoneStatus =
  | 'disabled'
  | 'loading'
  | 'device_pending'
  | 'locked_out'
  | 'ready'
  | 'ringing'
  | 'connecting'
  | 'live'
  | 'wrapup'
  | 'error';

export interface CtmActivityDetail {
  id?: number | string;
  caller_number?: string;
  caller_number_format?: string;
  contact_number?: string;
  name?: string;
  cnam?: string;
  direction?: string;
  [k: string]: unknown;
}

interface SoftphoneState {
  status: SoftphoneStatus;
  activity: CtmActivityDetail | null;
  callStartedAt: number | null;
  errorMessage: string;
  deviceVisible: boolean;
  deviceNote: string;
}

type SoftphoneAction =
  | { type: 'loading' }
  | { type: 'device_pending'; note: string }
  | { type: 'locked_out' }
  | { type: 'ready' }
  | { type: 'incoming'; activity: CtmActivityDetail | null }
  | { type: 'connecting'; activity: CtmActivityDetail | null }
  | { type: 'live'; activity: CtmActivityDetail | null }
  | { type: 'wrapup' }
  | { type: 'ended' }
  | { type: 'error'; message: string }
  | { type: 'device_visible'; visible: boolean };

const IN_CALL: SoftphoneStatus[] = ['ringing', 'connecting', 'live', 'wrapup'];

function softphoneReducer(state: SoftphoneState, action: SoftphoneAction): SoftphoneState {
  switch (action.type) {
    case 'loading':
      return { ...state, status: 'loading', errorMessage: '' };
    case 'device_pending':
      return IN_CALL.includes(state.status)
        ? state
        : { ...state, status: 'device_pending', deviceVisible: true, deviceNote: action.note, errorMessage: '' };
    case 'locked_out':
      return { ...state, status: 'locked_out', deviceVisible: true, deviceNote: 'The phone is already running in another window on this computer.' };
    case 'ready':
      return IN_CALL.includes(state.status)
        ? state
        : { ...state, status: 'ready', activity: null, callStartedAt: null, errorMessage: '', deviceNote: '' };
    case 'incoming':
      return state.status === 'live' || state.status === 'connecting'
        ? state
        : { ...state, status: 'ringing', activity: action.activity ?? state.activity };
    case 'connecting':
      return { ...state, status: 'connecting', activity: action.activity ?? state.activity };
    case 'live':
      return {
        ...state,
        status: 'live',
        activity: action.activity ?? state.activity,
        callStartedAt: state.callStartedAt ?? Date.now(),
      };
    case 'wrapup':
      return { ...state, status: 'wrapup', callStartedAt: null };
    case 'ended':
      return { ...state, status: 'ready', activity: null, callStartedAt: null };
    case 'error':
      return { ...state, status: 'error', errorMessage: action.message };
    case 'device_visible':
      return { ...state, deviceVisible: action.visible };
    default:
      return state;
  }
}

export interface SoftphoneContextValue {
  isEnabled: boolean;
  status: SoftphoneStatus;
  activity: CtmActivityDetail | null;
  callStartedAt: number | null;
  call: (number: string) => void;
  hangup: () => void;
  mute: () => void;
  open: () => void;
  minimize: () => void;
}

export const SoftphoneContext = createContext<SoftphoneContextValue>({
  isEnabled: false,
  status: 'disabled',
  activity: null,
  callStartedAt: null,
  call: () => console.warn('[Softphone] not enabled'),
  hangup: () => {},
  mute: () => {},
  open: () => {},
  minimize: () => {},
});

// Events the phone UI (control page) emits to its parent; the widget forwards
// every ctm:* message from its iframe to element listeners. The element
// overrides addEventListener with its own registry (nothing bubbles).
const CTM_EVENT_NAMES = [
  'ctm:ready',
  'ctm:device_registered',
  'ctm:stationCheck',
  'ctm:access_denied',
  'ctm:failed',
  'ctm:status',
  'ctm:unload',
  'ctm:accessTokenAssigned',
  'ctm:accessRequest',
  'ctm:requiresToken',
  'ctm:incomingCall',
  'ctm:screen-pop',
  'ctm:callWaiting',
  'ctm:connecting',
  'ctm:start',
  'ctm:answer',
  'ctm:live-activity',
  'ctm:end-activity',
  'ctm:hangup',
  'ctm:wrapup-start',
  'ctm:wrapup-end',
  'ctm:resize',
] as const;

interface LeaderApi {
  isLeader: boolean;
  takeOver: () => void;
}

/**
 * One tab per browser owns the phone. The lock is held for the tab's
 * lifetime; other tabs can take it over (steal) with one click.
 */
function useSoftphoneLeader(enabled: boolean): LeaderApi {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  const [isLeader, setIsLeader] = useState(() => !locks);
  const controllerRef = useRef<AbortController | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);

  const request = useCallback((steal: boolean) => {
    if (!locks) {
      setIsLeader(true);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const opts: LockOptions = steal ? { steal: true } : { signal: controller.signal };
    locks
      .request(LEADER_LOCK, opts, () => new Promise<void>((resolve) => {
        releaseRef.current = resolve;
        setIsLeader(true);
      }))
      .catch(() => {
        // AbortError: unmounted while waiting, or our lock was stolen
      })
      .finally(() => {
        if (controllerRef.current === controller) setIsLeader(false);
      });
  }, [locks]);

  useEffect(() => {
    if (!enabled) return;
    request(false);
    return () => {
      controllerRef.current?.abort();
      releaseRef.current?.();
      releaseRef.current = null;
      setIsLeader(false);
    };
  }, [enabled, request]);

  const takeOver = useCallback(() => request(true), [request]);
  return { isLeader, takeOver };
}

function isChromiumOrFirefox(): boolean {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return true;
  if (/Chrome\/|Chromium\/|Edg\//.test(ua)) return true;
  return false;
}

// ctm:incomingCall carries Twilio-style {parameters:{From, To, CallSid,...}};
// ctm:screen-pop / live-activity carry {activity:{...}} with CTM fields.
function normalizeActivity(detail: unknown): CtmActivityDetail | null {
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;
  const params = d.parameters as Record<string, unknown> | undefined;
  if (params && typeof params === 'object') {
    return {
      id: (params.CallSid as string) ?? (params.callSid as string) ?? undefined,
      caller_number: (params.From as string) ?? (params.Caller as string) ?? undefined,
      name: (params.CallerName as string) ?? (params.FromName as string) ?? undefined,
      direction: 'inbound',
      ...params,
    };
  }
  const activity = (d.activity as Record<string, unknown>) ?? d;
  return activity as CtmActivityDetail;
}

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { data: settings, isLoading: settingsLoading } = useCallTrackingSettings();
  const { isAdmin, isCSR } = useUserRole();

  const [optedOut] = useState(() => localStorage.getItem(SOFTPHONE_OPT_OUT_KEY) === 'true');
  const isEnabled = Boolean(
    !settingsLoading &&
    settings?.call_tracking_enabled &&
    settings?.softphone_enabled &&
    (isAdmin || isCSR) &&
    !optedOut,
  );
  const { isLeader, takeOver } = useSoftphoneLeader(isEnabled);
  const isActive = isEnabled && isLeader;
  const [browserOk] = useState(isChromiumOrFirefox);

  const [state, dispatch] = useReducer(softphoneReducer, {
    status: 'loading',
    activity: null,
    callStartedAt: null,
    errorMessage: '',
    deviceVisible: false,
    deviceNote: '',
  });
  const [isMinimized, setIsMinimized] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  const phoneRef = useRef<CtmPhoneEmbedElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshAtRef = useRef(0);
  const recoveryAttemptRef = useRef(0);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ringingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silentBootTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPhoneMsgRef = useRef(0);

  // ── Load the CTM embed script only when this tab runs the phone ────────────
  useEffect(() => {
    if (!isActive) return;
    if (customElements.get('ctm-phone-embed')) {
      setScriptLoaded(true);
      return;
    }
    if (!document.querySelector(`script[src="${CTM_EMBED_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = CTM_EMBED_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }
    let attempts = 0;
    const interval = setInterval(() => {
      if (customElements.get('ctm-phone-embed')) {
        setScriptLoaded(true);
        clearInterval(interval);
      } else if (++attempts >= 30) {
        dispatch({ type: 'error', message: 'Phone widget failed to load' });
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [isActive]);

  // ── Session minting (idempotent server-side) ───────────────────────────────
  const mintSession = useCallback(async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke<{ ticket_id: string }>(
        'ctm-create-phone-access-ticket',
        { method: 'POST' },
      );
      if (error || !data?.ticket_id) {
        dispatch({ type: 'error', message: error?.message || 'Could not start a phone session' });
        return null;
      }
      const url = `${SUPABASE_URL}/functions/v1/ctm-phone-access-via-ticket?ticket=${data.ticket_id}`;
      setAccessUrl(url);
      return url;
    } catch (err) {
      dispatch({ type: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isActive || !scriptLoaded) return;
    void mintSession();
  }, [isActive, scriptLoaded, mintSession]);

  // Refresh credentials in place: keep the session alive, point the live
  // element at it and ask the widget to fetch again. The widget's setter then
  // hands the token to the phone UI, which pushes it to the device over
  // their channel — no reload of anything.
  const refreshAccess = useCallback(async () => {
    const url = await mintSession();
    const el = phoneRef.current;
    if (!url || !el) return;
    el.setAttribute('access', url);
    try {
      await el.getUserAccess?.();
    } catch (err) {
      console.warn('[CTM] getUserAccess failed', err);
    }
  }, [mintSession]);

  const scheduleRefresh = useCallback((atMs: number) => {
    refreshAtRef.current = atMs;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max(30_000, atMs - Date.now());
    refreshTimerRef.current = setTimeout(() => void refreshAccess(), delay);
  }, [refreshAccess]);

  const scheduleRecovery = useCallback(() => {
    if (recoveryTimerRef.current) return;
    const attempt = recoveryAttemptRef.current;
    const delay = RECOVERY_BACKOFF_MS[Math.min(attempt, RECOVERY_BACKOFF_MS.length - 1)];
    recoveryAttemptRef.current = attempt + 1;
    recoveryTimerRef.current = setTimeout(() => {
      recoveryTimerRef.current = null;
      dispatch({ type: 'loading' });
      void refreshAccess();
    }, delay);
  }, [refreshAccess]);

  // Tabs in the background get their timers throttled; catch up on focus.
  useEffect(() => {
    if (!isActive) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (refreshAtRef.current && Date.now() >= refreshAtRef.current) void refreshAccess();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isActive, refreshAccess]);

  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    if (ringingTimerRef.current) clearTimeout(ringingTimerRef.current);
    if (silentBootTimerRef.current) clearTimeout(silentBootTimerRef.current);
  }, []);

  // ── Events from the phone UI (via the widget's registry) ───────────────────
  const handleCtmEvent = useCallback((name: string, payload: unknown) => {
    const detail = payload instanceof CustomEvent
      ? payload.detail
      : payload instanceof Event
        ? undefined
        : payload;
    console.debug(`[CTM] ${name}`, detail);
    if (name !== 'ctm:accessTokenAssigned' && name !== 'ctm:accessRequest') {
      lastPhoneMsgRef.current = Date.now();
    }

    switch (name) {
      case 'ctm:accessTokenAssigned': {
        // The widget wraps the access object one level deeper: {detail: access}
        const raw = detail as Record<string, unknown> | undefined;
        const access = (raw?.detail as Record<string, unknown> | undefined) ?? raw;
        if (!access?.token) {
          const reason = String(access?.error ?? access?.reason ?? access?.message ?? 'no token issued');
          dispatch({ type: 'error', message: `Phone access refused (${reason})` });
          scheduleRecovery();
          return;
        }
        recoveryAttemptRef.current = 0;
        const refreshAt = Number(access.sc_refresh_at);
        scheduleRefresh(Number.isFinite(refreshAt) && refreshAt > Date.now() ? refreshAt : Date.now() + DEFAULT_REFRESH_MS);
        // If the phone app stays silent, reveal the device so whatever it is
        // asking for (activate audio / station check) is visible.
        if (silentBootTimerRef.current) clearTimeout(silentBootTimerRef.current);
        const tokenAt = Date.now();
        silentBootTimerRef.current = setTimeout(() => {
          if (stateRef.current.status === 'loading' && lastPhoneMsgRef.current < tokenAt) {
            dispatch({ type: 'device_pending', note: 'Click the audio device below to activate the phone.' });
          }
        }, SILENT_BOOT_MS);
        break;
      }
      case 'ctm:ready':
      case 'ctm:device_registered':
        dispatch({ type: 'ready' });
        break;
      case 'ctm:stationCheck': {
        const mode = String((detail as { mode?: string } | undefined)?.mode ?? 'on');
        if (mode === 'on') {
          dispatch({ type: 'device_pending', note: 'Audio check needed — use the device panel below to activate the phone.' });
        }
        break;
      }
      case 'ctm:access_denied':
        dispatch({ type: 'error', message: 'Phone credentials expired — reconnecting…' });
        scheduleRecovery();
        break;
      case 'ctm:failed':
        dispatch({ type: 'error', message: 'Phone connection failed' });
        break;
      case 'ctm:unload':
        dispatch({ type: 'loading' });
        break;
      case 'ctm:incomingCall':
      case 'ctm:callWaiting': {
        dispatch({ type: 'incoming', activity: normalizeActivity(detail) });
        setIsMinimized(false);
        if (ringingTimerRef.current) clearTimeout(ringingTimerRef.current);
        ringingTimerRef.current = setTimeout(() => {
          if (stateRef.current.status === 'ringing') dispatch({ type: 'ended' });
        }, RINGING_WATCHDOG_MS);
        break;
      }
      case 'ctm:screen-pop':
        if (stateRef.current.status === 'ringing') {
          dispatch({ type: 'incoming', activity: normalizeActivity(detail) });
        }
        break;
      case 'ctm:connecting':
      case 'ctm:start':
      case 'ctm:answer':
        dispatch({ type: 'connecting', activity: normalizeActivity(detail) });
        break;
      case 'ctm:live-activity':
        dispatch({ type: 'live', activity: normalizeActivity(detail) });
        break;
      case 'ctm:wrapup-start':
        dispatch({ type: 'wrapup' });
        break;
      case 'ctm:end-activity':
      case 'ctm:hangup':
      case 'ctm:wrapup-end':
        dispatch({ type: 'ended' });
        break;
      default:
        // ctm:status / ctm:resize / ctm:accessRequest / ctm:requiresToken: log only
        break;
    }
  }, [scheduleRecovery, scheduleRefresh]);

  // Race-free listener attachment: runs synchronously when the element mounts.
  const attachPhoneRef = useCallback((el: HTMLElement | null) => {
    const prev = phoneRef.current as (CtmPhoneEmbedElement & { __scCleanup?: () => void }) | null;
    prev?.__scCleanup?.();

    phoneRef.current = el as CtmPhoneEmbedElement | null;
    if (!el) return;

    const bound = CTM_EVENT_NAMES.map((name) => {
      const fn = (payload: unknown) => handleCtmEvent(name, payload);
      el.addEventListener(name, fn as EventListener);
      return [name, fn] as const;
    });
    (el as CtmPhoneEmbedElement & { __scCleanup?: () => void }).__scCleanup = () => {
      bound.forEach(([name, fn]) => el.removeEventListener(name, fn as EventListener));
    };
  }, [handleCtmEvent]);

  // ── Messages from the audio device (posted to window.parent = us) ──────────
  useEffect(() => {
    if (!isActive) return;
    const onMsg = (e: MessageEvent) => {
      if (!/calltrackingmetrics\.com|\bctm\.com/.test(e.origin)) return;
      const data = e.data as { action?: string; data?: { mode?: string; progress?: number }; eventName?: string } | null;
      if (!data || typeof data !== 'object') return;
      lastPhoneMsgRef.current = Date.now();
      if (data.action) console.debug('[CTM device]', data.action, data.data ?? '');
      switch (data.action) {
        case 'ctm.device.locked_out':
          dispatch({ type: 'locked_out' });
          break;
        case 'ctm.device.station_check':
          if (data.data?.mode === 'on') {
            dispatch({ type: 'device_pending', note: 'Audio check needed — use the device panel below to activate the phone.' });
          }
          break;
        case 'ctm.device.inline_ready':
          dispatch({ type: 'ready' });
          dispatch({ type: 'device_visible', visible: false });
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [isActive]);

  // Once the device exists, stop the widget from re-running its device
  // bootstrap on later token refreshes (that path reloads the device iframe
  // with the new token URL and would drop an active call). Refreshed tokens
  // still reach the device through the phone UI's channel.
  useEffect(() => {
    if (!isActive || !accessUrl) return;
    const host = phoneRef.current;
    const container = host?.parentElement;
    if (!container) return;
    const check = () => {
      if (container.querySelector('ctm-device-embed')) {
        host?.setAttribute('suppress-device-embed', 'true');
        observer.disconnect();
      }
    };
    const observer = new MutationObserver(check);
    observer.observe(container, { childList: true });
    check();
    return () => observer.disconnect();
  }, [isActive, accessUrl]);

  // ── Context actions ────────────────────────────────────────────────────────
  const call = useCallback((number: string) => {
    const phone = phoneRef.current;
    const status = stateRef.current.status;
    if (!phone || ['loading', 'device_pending', 'locked_out', 'error', 'disabled'].includes(status)) {
      console.warn('[Softphone] phone not ready to dial', status);
      setIsMinimized(false);
      return;
    }
    if (typeof phone.dialTo === 'function') phone.dialTo(number);
    else phone.call?.(number);
    setIsMinimized(false);
  }, []);
  const hangup = useCallback(() => phoneRef.current?.hangup?.(), []);
  const mute = useCallback(() => phoneRef.current?.mute?.(), []);
  const open = useCallback(() => setIsMinimized(false), []);
  const minimize = useCallback(() => setIsMinimized(true), []);

  useEffect(() => {
    if (!isActive) return;
    const w = window as unknown as Record<string, unknown>;
    w.__crmOpenPhone = open;
    w.__crmCall = call;
    return () => {
      delete w.__crmOpenPhone;
      delete w.__crmCall;
    };
  }, [isActive, open, call]);

  const contextValue = useMemo<SoftphoneContextValue>(() => ({
    isEnabled,
    status: isActive ? state.status : 'disabled',
    activity: state.activity,
    callStartedAt: state.callStartedAt,
    call,
    hangup,
    mute,
    open,
    minimize,
  }), [isEnabled, isActive, state.status, state.activity, state.callStartedAt, call, hangup, mute, open, minimize]);

  const inCall = IN_CALL.includes(state.status);
  const showDevice = state.deviceVisible;

  const statusStrip = (() => {
    switch (state.status) {
      case 'loading':
        return { icon: <Loader2 className="h-3 w-3 animate-spin" />, text: 'Connecting to phone…', tone: 'muted' as const };
      case 'device_pending':
        return { icon: <Headphones className="h-3 w-3" />, text: state.deviceNote || 'Activate the audio device below.', tone: 'warning' as const };
      case 'locked_out':
        return { icon: <PhoneOff className="h-3 w-3" />, text: state.deviceNote, tone: 'warning' as const };
      case 'error':
        return { icon: <PhoneOff className="h-3 w-3" />, text: state.errorMessage || 'Phone unavailable', tone: 'error' as const };
      default:
        return null;
    }
  })();

  return (
    <SoftphoneContext.Provider value={contextValue}>
      {children}
      {isEnabled && !isLeader && (
        <Button
          variant="secondary"
          size="icon"
          className="fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg"
          title="Phone is open in another tab — click to take it over here"
          onClick={takeOver}
        >
          <Phone className="h-5 w-5 opacity-60" />
        </Button>
      )}
      {isActive && (
        <>
          {state.status === 'ringing' && (
            <IncomingCallPop activity={state.activity} onOpenPhone={open} />
          )}

          {/* Always mounted at full size (the widget's iframes must never
              initialise at 0x0); minimizing only hides it visually. */}
          <div
            className={cn(
              'fixed bottom-4 right-4 z-40 w-[400px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
              showDevice ? 'h-[780px]' : 'h-[600px]',
              'bg-card border rounded-lg shadow-xl flex flex-col overflow-hidden transition-opacity duration-150',
              isMinimized && 'opacity-0 pointer-events-none',
            )}
          >
            <div className="bg-primary text-primary-foreground px-3 py-2 flex items-center justify-between shrink-0">
              <span className="text-sm font-medium">Phone</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn('h-6 w-6 p-0 text-primary-foreground hover:bg-primary/80', showDevice && 'bg-primary/70')}
                  onClick={() => dispatch({ type: 'device_visible', visible: !showDevice })}
                  title={showDevice ? 'Hide audio device panel' : 'Show audio device panel (station check, audio test)'}
                >
                  <Headphones className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-primary-foreground hover:bg-primary/80"
                  onClick={minimize}
                  title="Minimize"
                >
                  −
                </Button>
              </div>
            </div>

            {!browserOk && (
              <div className="px-3 py-1.5 text-xs text-warning bg-warning/10 border-b shrink-0">
                The phone is supported in Chrome, Edge or Firefox.
              </div>
            )}
            {statusStrip && (
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 text-xs border-b shrink-0',
                  statusStrip.tone === 'muted' && 'text-muted-foreground bg-muted',
                  statusStrip.tone === 'warning' && 'text-warning bg-warning/10',
                  statusStrip.tone === 'error' && 'text-destructive bg-destructive/10',
                )}
              >
                {statusStrip.icon}
                <span className="truncate">{statusStrip.text}</span>
                {state.status === 'error' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 ml-auto text-xs"
                    onClick={() => {
                      recoveryAttemptRef.current = 0;
                      dispatch({ type: 'loading' });
                      void refreshAccess();
                    }}
                  >
                    Retry
                  </Button>
                )}
              </div>
            )}

            {/* Widget host: the phone UI (ctm-phone-embed) and, once a token
                arrives, the audio device (ctm-device-embed) inserted as its
                sibling. Layout rules live in index.css (.softphone-embed). */}
            <div className={cn('softphone-embed', showDevice && 'device-visible')}>
              {accessUrl && (
                <ctm-phone-embed
                  ref={attachPhoneRef}
                  access={accessUrl}
                  auto="true"
                  defer-screen-pop="off"
                />
              )}
            </div>
          </div>

          {isMinimized && (
            <Button
              variant="default"
              size="icon"
              className={cn(
                'fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg',
                inCall && 'animate-pulse bg-success hover:bg-success/90',
                (state.status === 'device_pending' || state.status === 'error' || state.status === 'locked_out') &&
                  'ring-2 ring-warning',
              )}
              onClick={open}
              title={state.status === 'device_pending' ? 'Phone needs attention — click to activate audio' : 'Open phone'}
            >
              <Phone className="h-5 w-5" />
            </Button>
          )}
        </>
      )}
    </SoftphoneContext.Provider>
  );
}
